import { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import Loader from '../components/Loader';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useRegisterMutation } from '../slices/usersApiSlice';
import { setCredentials } from '../slices/authSlice';
import { toast } from 'react-toastify';
import { trackUserSignup } from '../utils/analytics';

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const HONEYPOT_STYLE = {
  position: 'absolute',
  left: '-9999px',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
};

const RegisterScreen = () => {
  const [username, setUserName] = useState('');
  const [firstname, setFirstName] = useState('');
  const [lastname, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userExistsError, setUserExistsError] = useState(false);
  const [website, setWebsite] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const turnstileSiteKey = String(import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [register, { isLoading }] = useRegisterMutation();

  const { userInfo } = useSelector((state) => state.auth);
  const authIntent = searchParams.get('intent');
  const redirect = searchParams.get('redirect');

  const getPostAuthPath = () => {
    if (redirect) {
      return redirect;
    }

    if (authIntent === 'create-league' || authIntent === 'join-league') {
      return `/leagues?intent=${authIntent}`;
    }

    return '/leagues';
  };

  const buildAuthPath = (path) => {
    const nextParams = new URLSearchParams();

    if (authIntent) {
      nextParams.set('intent', authIntent);
    }

    if (redirect) {
      nextParams.set('redirect', redirect);
    }

    return `${path}${nextParams.toString() ? `?${nextParams.toString()}` : ''}`;
  };

  useEffect(() => {
    // Remove navigate('/') here to prevent overriding the athlete screen navigation
    // This effect was likely redirecting to '/' immediately after registration due to setCredentials
    
    // Reset user exists error when component mounts or userInfo changes
    setUserExistsError(false);
  }, [userInfo]);

  useEffect(() => {
    if (!turnstileSiteKey || !turnstileContainerRef.current) {
      return undefined;
    }

    let disposed = false;
    let scriptEl = document.getElementById(TURNSTILE_SCRIPT_ID);

    const resetToken = () => setTurnstileToken('');

    const renderWidget = () => {
      if (disposed || !window.turnstile || turnstileWidgetIdRef.current !== null || !turnstileContainerRef.current) {
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': resetToken,
        'error-callback': resetToken,
      });
    };

    const handleScriptLoad = () => {
      renderWidget();
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = TURNSTILE_SCRIPT_ID;
        scriptEl.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        scriptEl.async = true;
        scriptEl.defer = true;
        document.body.appendChild(scriptEl);
      }

      scriptEl.addEventListener('load', handleScriptLoad);
    }

    return () => {
      disposed = true;

      if (scriptEl) {
        scriptEl.removeEventListener('load', handleScriptLoad);
      }

      if (window.turnstile && turnstileWidgetIdRef.current !== null) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [turnstileSiteKey]);

  const resetTurnstile = () => {
    setTurnstileToken('');

    if (window.turnstile && turnstileWidgetIdRef.current !== null) {
      window.turnstile.reset(turnstileWidgetIdRef.current);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (turnstileSiteKey && !turnstileToken) {
      toast.error('Please complete the security check');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
    } else {
      try {
        const res = await register({
          username,
          firstname,
          lastname,
          email,
          password,
          website,
          turnstileToken,
        }).unwrap();

        // Assuming the athleteId is included in the response
        const athleteId = res.athleteId;

        // Dispatching credentials to the store
        dispatch(setCredentials({ userInfo: res }));

        // Track successful signup
        trackUserSignup(res._id);

        // Navigate to the athlete screen with the correct query parameter
        if (athleteId) {
          navigate(getPostAuthPath());
        } else {
          toast.error('Failed to retrieve athlete information.');
        }
      } catch (err) {
        if (turnstileSiteKey) {
          resetTurnstile();
        }

        // Check specifically for "User already exists" error
        if (err?.data?.message === 'User already exists') {
          setUserExistsError(true);
          toast.error('This email is already registered. Please sign in instead.', {
            autoClose: 5000, // Stay visible longer
          });
        } else {
          setUserExistsError(false);
          toast.error(err?.data?.message || err.error);
        }
      }
    }
  };

  return (
    <div className="section-modern">
      <div className="container-modern">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="form-modern fade-in">
              <div className="text-center mb-4">
                <h1 className="text-primary mb-2">Join League Runner</h1>
                <p className="text-secondary">Create your athlete account</p>
              </div>

              <div className="alert alert-warning mb-4" role="alert">
                League Runner works on your phone, but the experience is better on a larger screen. A tablet or laptop is especially recommended for live drafts.
              </div>

              <Form onSubmit={submitHandler}>
                <div className="grid-modern grid-cols-2">
                  <div className="form-group-modern">
                    <label htmlFor="firstname" className="form-label-modern">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstname"
                      className="form-input-modern"
                      placeholder="Enter your first name"
                      value={firstname}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group-modern">
                    <label htmlFor="lastname" className="form-label-modern">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastname"
                      className="form-input-modern"
                      placeholder="Enter your last name"
                      value={lastname}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group-modern">
                  <label htmlFor="username" className="form-label-modern">
                    Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    className="form-input-modern"
                    placeholder="Choose a username"
                    value={username}
                    onChange={(e) => setUserName(e.target.value)}
                    required
                  />
                </div>

                <div style={HONEYPOT_STYLE} aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>

                <div className="form-group-modern">
                  <label htmlFor="email" className="form-label-modern">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    className={`form-input-modern ${userExistsError ? 'border-danger' : ''}`}
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (userExistsError) setUserExistsError(false);
                    }}
                    required
                  />
                  {userExistsError && (
                    <div className="text-danger mt-1">
                      This email is already registered. <Link to={buildAuthPath('/login')} className="fw-medium">Sign in instead</Link>
                    </div>
                  )}
                </div>

                <div className="grid-modern grid-cols-2">
                  <div className="form-group-modern">
                    <label htmlFor="password" className="form-label-modern">
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      className="form-input-modern"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group-modern">
                    <label htmlFor="confirmPassword" className="form-label-modern">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      className="form-input-modern"
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {turnstileSiteKey && (
                  <div className="mb-3">
                    <div ref={turnstileContainerRef} />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary-modern w-100 mb-3"
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </button>
              </Form>

              {isLoading && (
                <div className="text-center py-3">
                  <Loader />
                </div>
              )}

              <div
                className="text-center mt-4 pt-4"
                style={{ borderTop: '1px solid var(--border-light)' }}
              >
                <p className="text-secondary mb-0">
                  Already have an account?{' '}
                  <Link
                    to={buildAuthPath('/login')}
                    className="text-decoration-none fw-medium"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterScreen;
